import { Component, Input, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PricingService } from '@shared/services/pricing.service';
import { ShipmentService } from '@shared/services/shipment.service';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/redux/config-store-state.model';
import { TranslateService } from '@ngx-translate/core';
import { emitAlert } from '@shared/redux/actions/actions-store-state.model';
import { BookMerge, CartStorage, CartStorageService } from '@shared/services/cart-storage.service';
import { Router } from '@angular/router';
import { DataOption, Country, getDataCountries, getDataProvinces } from '@shared/data-structure/profile-data';
import { CartApiService } from '@modules/cart/cart-api.service';
import { PayMethodType, ShipmentType } from '@shared/data-structure/cart-types';
import { IPayPalConfig, ICreateOrderRequest } from 'ngx-paypal';
import { PayPalService, PayPalInfo } from '@shared/services/paypal.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'astrea-cart-physical',
  templateUrl: './cart-physical.component.html',
  styleUrls: ['../cart/cart.component.scss']
})
export class CartPhysicalComponent implements OnInit {

  public books: BookMerge[] = [];
  public totalPhysical: number = 0;

  public step = 0;
  public validUser = false;

  public isTouch = false;

  public countries!: Country[];
  public provinces!: DataOption[];

  public shipmentMethod!: ShipmentType | null;
  public payMethod!: PayMethodType | null;

  public preparingPaid = false;

  @Input() isLoggedIn!: boolean;
  @Input() set physicalBooks(books: BookMerge[]) {
    this.books = books;
    this.setTotalPrice();
  }

  public user: any;
  @Input()
    set setUser(u: any) {
      this.user = u;
      this.shipmentMethod = null;
      this.payMethod = null;
    };

  @Input() userInvoicing!: boolean;
  @Input() userResidence!: boolean;

  public payPalConfig?: IPayPalConfig;
  public payPalInfo?: PayPalInfo;
  public paypalLoading = false;

  constructor(
    private router: Router,
    public pricingService: PricingService,
    public shipmentService: ShipmentService,
    private api: CartApiService,
    private store: Store<AppState>,
    private payPalService: PayPalService,
    private cartStorageService: CartStorageService,
    private _t: TranslateService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) { }

  ngOnInit(): void {
    this.store.select(state => state.storeConfig.currency)
      .subscribe({
        next: () => {
          this.setTotalPrice();
          this.getPayPalInfo();
        }
      });
  }

  setTotalPrice(): void {
    this.totalPhysical = 0;
    this.books.forEach((b: BookMerge) => {
      if (b.cartInfo.typeStorage == 'physical_book')
        this.totalPhysical += this.pricingService.getWebPricing(b.bookInfo) * b.cartInfo.count;
    });
  }

  async getPayPalInfo() {
    this.payPalInfo = await this.payPalService.buildPaymentInfo(this.books, 'physical');
  }


  /* ********** SHIPMENT METHOD ********** */

  stepShipmentMethod(): void {
    if (!this.userInvoicing || !this.userResidence) {
      this.isTouch = true;
      this.dispatchUserData();
    }
  }

  setShipmentMethod(shipment: ShipmentType): void {
    this.shipmentMethod = shipment;
  }


  /* ********** PAID METHOD ********** */

  stepPaidMethod(): void {
    if (!this.shipmentMethod)
      this.dispatchSelectMethod();
  }

  setPayMethod(pay: PayMethodType): void {
    this.payMethod = pay;

    if (pay == 'paypal')
      this.initPayPalConfig();
  }


  /* ********** CART FLOW ********** */

  nextStep(step: number): void {
    this.step = step;
  }

  confirmPay(): void {
    this.preparingPaid = true;

    const cartInfo: CartStorage[] = this.books.map(b => b.cartInfo);

    let account = 'arg';
    if (this.user && this.user.nationality && this.user.nationality == 'CO')
      account = 'col';

    if (this.payMethod == 'mercado-pago') {

      if (this.shipmentMethod == 'correo-argentino') {
        this.api.generatePayMP_Physical({ books: cartInfo, account, typePurchase: 'shipment' })
          .subscribe({
            next: res => {
              this.preparingPaid = false;
              if (environment.env == 'production')
                window.location.href = res.result.data.init_point;
              else
                window.location.href = res.result.data.sandbox_init_point;
            },
            error: () => {
              this.dispatchError();
              this.cartStorageService.resetCartPhysical();
              setTimeout(() => { window.location.reload(); }, 3000);
            }
          });

      } else if (this.shipmentMethod == 'house') {
        this.api.generatePayMP_Physical({ books: cartInfo, account, typePurchase: 'house' })
          .subscribe({
            next: res => {
              this.preparingPaid = false;
              if (environment.env == 'production')
                window.location.href = res.result.data.init_point;
              else
                window.location.href = res.result.data.sandbox_init_point;
            },
            error: () => {
              this.dispatchError();
              this.cartStorageService.resetCartPhysical();
              setTimeout(() => { window.location.reload(); }, 3000);
            }
          });
      }

    } else if (this.payMethod == 'cash') {

      if (this.shipmentMethod == 'correo-argentino' || this.shipmentMethod == 'house') {
        this.api.registryPurchaseCash(cartInfo, this.shipmentMethod)
          .subscribe({
            next: res => {
              this.preparingPaid = false;
              if (res.result.data && res.result.data._id) {
                setTimeout(() => {
                  this.router.navigate(['/cash-success'], { queryParams: { purchaseId: res.result.data._id } });
                }, 0);
              }
            },
            error: () => {
              this.dispatchError();
              this.cartStorageService.resetCartPhysical();
              setTimeout(() => { window.location.reload(); }, 3000);
            }
          });
      }

    }
  }


  /* ********** PAYPAL ********** */

  private async initPayPalConfig() {

    let account = '';
    let clientID = '';
    if (this.user && this.user.nationality && this.user.nationality != 'CO') {
      account = 'col';
      clientID = environment.paypal.clientIdCOL;
    } else {
      // Si el usuario es de Colombia, DEBERIAMOS usar otra cuenta de PayPal
      account = 'col';
      clientID = environment.paypal.clientIdCOL;
    }

    const currency = this.pricingService.getCurrencyPayPal();

    // Volvemos a actualizar payPalInfo para asegurar de que está actualizado la cantidad de items y el shipment, si no hay errores de async
    await this.getPayPalInfo();

    this.payPalConfig = {
      currency: currency,
      clientId: clientID,
      createOrderOnClient: (data) => <ICreateOrderRequest> {
        intent: 'CAPTURE',
        purchase_units: [{
          amount: {
            currency_code: this.payPalInfo?.amount.currency_code,
            value: this.payPalInfo?.amount.value,
            breakdown: {
              item_total: {
                currency_code: this.payPalInfo?.amount.currency_code,
                value: this.payPalInfo?.amount.value
              }
            }
          },
          items: this.payPalInfo?.items
        }]
      },
      advanced: {
        commit: 'true'
      },
      style: {
        label: 'paypal',
        layout: 'vertical'
      },
      onApprove: (data, actions) => {},
      onClientAuthorization: (data) => {
        const paymentID = data.id;
        this.paypalLoading = true;
        if (data.status == 'COMPLETED') {
          this.api.registryPurchasePaypalPhysical({
            account: account,
            paymentID: paymentID,
          })
            .subscribe({
              next: () => {
                this.paypalLoading = false;
                this.router.navigate(['/pp-success/shipment'], { queryParams: { paymentID } });
              }
            });
        }
      },
      onError: err => {
        if (this.isBrowser && err.message.includes('Unexpected currency')) {
          this.dispatchErrorPaypalCurrency();
          setTimeout(() => { window.location.reload(); }, 3000);
        } else {
          this.dispatchErrorPaypal();
        }
      },
      onCancel: (data, actions) => {},
      onClick: (data, actions) => {}
    };
  }


  /* ********** ALERTS ********** */

  dispatchUserData(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.user_data'),
      alertType: 'message'
    }));
  }

  dispatchSelectMethod(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.payment'),
      alertType: 'message'
    }));
  }

  dispatchErrorPaypal(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.err_payment'),
      alertType: 'danger'
    }));
  }

  dispatchErrorPaypalCurrency(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.err_pay_reload'),
      alertType: 'danger'
    }));
  }

  dispatchError(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart.err_mp'),
      alertType: 'danger'
    }));
  }


  /* ********************
         FUNCTIONS
  ******************** */

  getCountry(id: string): string {
    const countries: Country[] = getDataCountries();
    return countries.filter(c => { return c.value == id })[0]!.text;
  }

  getProvince(id: string): string {
    const provinces: DataOption[] = getDataProvinces();
    return provinces.filter(p => { return p.value == id })[0]!.text;
  }

  public get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

}
